import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { BeakerIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { ELEMENTS, GROUP_LABELS } from "./periodicTableData";

const BLOCKS = ["s", "p", "d", "f"];
// The school colours of the electron families. The ring is kept apart: a selected cell replaces it.
const BLOCK_TONES = {
  s: "bg-rose-100 text-rose-950",
  p: "bg-amber-100 text-amber-950",
  d: "bg-sky-100 text-sky-950",
  f: "bg-emerald-100 text-emerald-950",
};
const BLOCK_RINGS = { s: "ring-rose-200", p: "ring-amber-200", d: "ring-sky-200", f: "ring-emerald-200" };

// Cells grow with the screen height; the names of the elements show from 1600×940, where they fit.
const CELL = "clamp(2.75rem, 6.6vh, 4.25rem)";
const GRID_STYLE = {
  gridTemplateColumns: "1.25rem repeat(18, minmax(2.75rem, 1fr))",
  gridTemplateRows: `auto repeat(7, ${CELL}) 0.375rem repeat(2, ${CELL})`,
};
const SYMBOL_STYLE = { fontSize: "clamp(0.95rem, 2.1vh, 1.3rem)", lineHeight: 1 };
const PLACEHOLDERS = [
  ["57–71", "La–Lu", 7],
  ["89–103", "Ac–Lr", 8],
];

const BY_NUMBER = new Map(ELEMENTS.map((el) => [el.number, el]));
const AT_CELL = new Map(ELEMENTS.map((el) => [`${el.row}:${el.col}`, el.number]));
const ARROWS = { ArrowLeft: [0, -1], ArrowRight: [0, 1], ArrowUp: [-1, 0], ArrowDown: [1, 0] };

// The nearest element from `number` in a direction, across the gaps of the table; at an edge, itself.
const stepFrom = (number, [dr, dc]) => {
  const el = BY_NUMBER.get(number);
  for (let r = el.row + dr, c = el.col + dc; r >= 2 && r <= 11 && c >= 2 && c <= 19; r += dr, c += dc) {
    const next = AT_CELL.get(`${r}:${c}`);
    if (next) return next;
  }
  return number;
};

const nameLang = (lng = "") => (lng.startsWith("ru") ? "ru" : lng.startsWith("en") ? "en" : "kk");
const formatMass = (mass, lang) => (lang === "en" ? mass : mass.replace(".", ","));
const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

// The empty corner of the table (groups 3–12, periods 1–3): the element under the pointer or the selected
// one, enlarged; before any, what a cell shows.
function Details({ el, lang, style }) {
  const { t } = useTranslation();
  const card = "flex min-w-0 items-center gap-4 rounded-xl bg-slate-50 p-2 ring-1 ring-inset ring-slate-200";

  if (!el) {
    return (
      <div style={style} className={card}>
        <div className="flex aspect-square h-full shrink-0 flex-col justify-between rounded-lg border border-dashed border-slate-300 bg-white p-2 text-center text-slate-500">
          <span className="text-left text-xs font-semibold">Z</span>
          <span className="text-sm font-semibold">{t("exam_taking.periodic_table.symbol")}</span>
          <span className="truncate text-[11px]">{t("exam_taking.periodic_table.name")}</span>
          <span className="text-xs font-semibold">
            A<sub>r</sub>
          </span>
        </div>
        <div className="min-w-0 space-y-1 text-xs leading-snug text-slate-600">
          <p>
            <b>Z</b> — {t("exam_taking.periodic_table.number")}
          </p>
          <p>
            <b>
              A<sub>r</sub>
            </b>{" "}
            — {t("exam_taking.periodic_table.mass")}
          </p>
          <p>
            <b>[ ]</b> — {t("exam_taking.periodic_table.isotope")}
          </p>
          <p className="pt-1 text-slate-400">{t("exam_taking.periodic_table.hint")}</p>
        </div>
      </div>
    );
  }

  const name = el.names[lang];
  const mass = formatMass(el.mass, lang);
  return (
    <div style={style} className={card}>
      <div className={`flex aspect-square h-full shrink-0 flex-col justify-between rounded-lg p-2 ring-1 ring-inset ${BLOCK_TONES[el.block]} ${BLOCK_RINGS[el.block]}`}>
        <span className="text-sm font-semibold tabular-nums">{el.number}</span>
        <span className="text-center font-bold" style={{ fontSize: "clamp(1.75rem, 4.4vh, 3rem)", lineHeight: 1 }}>
          {el.symbol}
        </span>
        <span className="truncate text-center text-xs">{name}</span>
        <span className="text-center text-sm font-semibold tabular-nums">{mass}</span>
      </div>
      <div className="min-w-0 space-y-1 text-sm text-slate-700">
        <p className="truncate text-lg font-semibold text-slate-900">{name}</p>
        <p>
          A<sub>r</sub> = <span className="font-semibold tabular-nums">{mass}</span>
        </p>
        <p>
          {t("exam_taking.periodic_table.period")}: {el.period} · {t("exam_taking.periodic_table.group")}: {el.group} ({GROUP_LABELS[el.group - 1]})
        </p>
        <p>{t("exam_taking.periodic_table.block", { block: el.block })}</p>
        {el.mass.startsWith("[") && <p className="text-xs text-slate-500">[ ] — {t("exam_taking.periodic_table.isotope")}</p>}
      </div>
    </div>
  );
}

/**
 * The periodic table of a test that allows it. Like the calculator, a round button opens it in a panel
 * over the page. The panel moves by its title bar and leaves room for the calculator's button or panel.
 */
export default function PeriodicTable({ calculatorAllowed = false, calculatorOpen = false }) {
  const { t, i18n } = useTranslation();
  const lang = nameLang(i18n.resolvedLanguage || i18n.language);
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [hovered, setHovered] = useState(null);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const panelRef = useRef(null);
  const toggleRef = useRef(null);
  const wasOpenRef = useRef(false);
  const cellRefs = useRef(new Map());
  const dragRef = useRef(null);

  // As with the calculator: the open panel takes the focus, so digits typed next don't answer the
  // question, and the button gets it back when the panel closes.
  useEffect(() => {
    if (isOpen) panelRef.current?.focus();
    else if (wasOpenRef.current) toggleRef.current?.focus();
    wasOpenRef.current = isOpen;
  }, [isOpen]);

  // A moved panel could end up outside a resized window: it goes back to its place.
  useEffect(() => {
    const reset = () => setOffset({ x: 0, y: 0 });
    window.addEventListener("resize", reset);
    return () => window.removeEventListener("resize", reset);
  }, []);

  const startDrag = (e) => {
    if (e.button !== 0 || e.target.closest("button")) return;
    dragRef.current = { x: e.clientX, y: e.clientY, from: offset, rect: panelRef.current.getBoundingClientRect() };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const drag = (e) => {
    const start = dragRef.current;
    if (!start) return;
    const { rect } = start;
    // The close button stays on screen: the right edge does not leave the window, the left one may (8rem of
    // the title bar stay), and the panel may sink to its title bar.
    setOffset({
      x: start.from.x + clamp(e.clientX - start.x, 128 - rect.right, window.innerWidth - 8 - rect.right),
      y: start.from.y + clamp(e.clientY - start.y, 8 - rect.top, window.innerHeight - 48 - rect.top),
    });
  };
  const endDrag = () => {
    dragRef.current = null;
  };

  // Arrow keys move between the elements; Tab enters and leaves the table at the selected one.
  const moveFocus = (e) => {
    const dir = ARROWS[e.key];
    const number = Number(e.target.dataset?.number);
    if (!dir || !number) return;
    e.preventDefault();
    const next = stepFrom(number, dir);
    setSelected(next);
    cellRefs.current.get(next)?.focus();
  };

  if (!isOpen) {
    // Next to the calculator's button, or left of its open panel (on a phone that panel fills the width).
    const spot = !calculatorAllowed ? "right-5" : calculatorOpen ? "right-[22.25rem] max-sm:hidden" : "right-[5.75rem]";
    return (
      <button
        ref={toggleRef}
        type="button"
        onClick={() => setIsOpen(true)}
        className={`fixed bottom-5 z-40 rounded-full bg-blue-600 p-3.5 text-white shadow-lg shadow-blue-600/30 ring-4 ring-white transition duration-200 hover:bg-blue-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 ${spot}`}
        title={t("exam_taking.periodic_table.title")}
        aria-label={t("exam_taking.periodic_table.title")}
      >
        <BeakerIcon className="h-6 w-6" />
      </button>
    );
  }

  const active = BY_NUMBER.get(hovered ?? selected) || null;
  const tabbable = selected ?? 1;
  // Room on the right for the calculator's button or its open panel.
  const reserve = !calculatorAllowed ? "2.5rem" : calculatorOpen ? "23.25rem" : "7rem";

  // ExamSession's shortcuts ignore keys typed in here (data-exam-tool).
  return (
    <div
      ref={panelRef}
      data-exam-tool
      tabIndex={-1}
      role="region"
      aria-label={t("exam_taking.periodic_table.title")}
      style={{
        width: `min(84rem, calc(100vw - ${reserve}))`,
        transform: offset.x || offset.y ? `translate(${offset.x}px, ${offset.y}px)` : undefined,
      }}
      className={`fixed left-2 right-2 z-40 flex max-h-[calc(100vh-6rem)] select-none flex-col overflow-hidden rounded-2xl bg-white shadow-2xl outline-none ring-1 ring-slate-900/10 max-sm:!w-auto sm:bottom-5 sm:left-5 sm:right-auto sm:max-h-[calc(100vh-7rem)] ${
        calculatorAllowed ? "bottom-20" : "bottom-2"
      }`}
    >
      <div
        onPointerDown={startDrag}
        onPointerMove={drag}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        title={t("exam_taking.periodic_table.move")}
        className="flex touch-none items-center gap-3 border-b border-slate-200 px-3 py-2 sm:cursor-grab sm:active:cursor-grabbing"
      >
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
          <BeakerIcon className="h-4 w-4" aria-hidden="true" />
        </span>
        <span className="min-w-0 flex-1 truncate text-sm font-semibold text-slate-900">{t("exam_taking.periodic_table.title")}</span>
        <ul className="hidden items-center gap-3 md:flex">
          {BLOCKS.map((block) => (
            <li key={block} className="inline-flex items-center gap-1.5 text-xs text-slate-600">
              <span className={`h-3 w-3 rounded-sm ring-1 ring-inset ${BLOCK_TONES[block]} ${BLOCK_RINGS[block]}`} aria-hidden="true" />
              {t("exam_taking.periodic_table.blocks", { block })}
            </li>
          ))}
        </ul>
        <button
          type="button"
          onClick={() => setIsOpen(false)}
          aria-label={t("buttons.close")}
          className="rounded-lg p-1.5 text-slate-400 transition duration-200 hover:bg-slate-100 hover:text-slate-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-600"
        >
          <XMarkIcon className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>

      <div className="min-h-0 flex-1 overflow-auto p-3">
        <div className="grid min-w-[54.25rem] gap-[3px]" style={GRID_STYLE} onMouseLeave={() => setHovered(null)} onKeyDown={moveFocus}>
          {GROUP_LABELS.map((label, i) => (
            <div key={`g${i}`} style={{ gridRow: 1, gridColumn: i + 2 }} className="flex flex-col items-center justify-end pb-1 leading-none">
              <span className="text-[11px] font-semibold tabular-nums text-slate-700">{i + 1}</span>
              <span className="mt-0.5 text-[9px] text-slate-400">{label}</span>
            </div>
          ))}
          {[1, 2, 3, 4, 5, 6, 7].map((period) => (
            <div key={`p${period}`} style={{ gridRow: period + 1, gridColumn: 1 }} className="flex items-center justify-center text-[11px] font-semibold tabular-nums text-slate-500">
              {period}
            </div>
          ))}

          <Details el={active} lang={lang} style={{ gridRow: "2 / 5", gridColumn: "4 / 14" }} />

          {PLACEHOLDERS.map(([numbers, symbols, row]) => (
            <div
              key={numbers}
              aria-hidden="true"
              style={{ gridRow: row, gridColumn: 4 }}
              className="flex flex-col items-center justify-center rounded-md border border-dashed border-emerald-300 bg-emerald-50 text-[10px] leading-tight text-emerald-900"
            >
              <span className="tabular-nums">{numbers}</span>
              <span className="font-semibold">{symbols}</span>
            </div>
          ))}
          {[
            ["lanthanides", 10],
            ["actinides", 11],
          ].map(([key, row]) => (
            <div key={key} style={{ gridRow: row, gridColumn: "1 / 4" }} className="flex items-center justify-end pr-1.5 text-right text-[11px] font-medium leading-tight text-slate-600">
              {t(`exam_taking.periodic_table.${key}`)}
            </div>
          ))}

          {ELEMENTS.map((el) => {
            const isSelected = el.number === selected;
            const mass = formatMass(el.mass, lang);
            return (
              <button
                key={el.number}
                ref={(node) => (node ? cellRefs.current.set(el.number, node) : cellRefs.current.delete(el.number))}
                type="button"
                data-number={el.number}
                tabIndex={el.number === tabbable ? 0 : -1}
                aria-pressed={isSelected}
                aria-label={`${el.number}, ${el.symbol}, ${el.names[lang]}, ${mass}`}
                onClick={() => setSelected(el.number)}
                onFocus={() => setSelected(el.number)}
                onMouseEnter={() => setHovered(el.number)}
                style={{ gridRow: el.row, gridColumn: el.col }}
                className={`flex min-w-0 flex-col justify-between whitespace-nowrap rounded-md px-0.5 py-[3px] leading-none ring-inset transition duration-150 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-blue-600 ${
                  BLOCK_TONES[el.block]
                } ${isSelected ? "relative z-10 ring-2 ring-blue-600" : `ring-1 ${BLOCK_RINGS[el.block]} hover:ring-2 hover:ring-slate-400`}`}
              >
                <span className="pl-0.5 text-left text-[10px] tabular-nums opacity-70">{el.number}</span>
                <span className="text-center font-bold" style={SYMBOL_STYLE}>
                  {el.symbol}
                </span>
                <span className="hidden truncate text-center text-[9px] [@media(min-width:1600px)_and_(min-height:940px)]:block">{el.names[lang]}</span>
                <span className="text-center text-[10px] tabular-nums">{mass}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
